import { Dropdown, Menu, Tooltip } from 'antd';
import {
  useCallback, useEffect, useMemo, useState,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AppConstants } from 'constants/index';
import { locales } from 'constants/mapping';
import { useTimeZone } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { useLazyGetLocaleResourcesQuery } from 'app/locale/api';
import { selectUserConfigLanguageId } from 'app/userPreferences/selectors';
import { useCreateOrEditUserConfigMutation } from 'app/userPreferences/api';
import { StyledListItemHeader, StyleFlagsWrapper } from 'app/layout/components/Layout.styled';
import { StyledFlag, StyledFlagWrapper, StyledLanguageName } from 'shared/icon/Icon.styled';

const { languages } = AppConstants;

type LanguageKey = keyof typeof languages;

const Languages = () => {
  const [open, setOpen] = useState(false);
  const { date } = useTimeZone();
  const { i18n } = useTranslation();
  const { changeLanguage } = i18n;
  const language = useAppSelector(selectUserConfigLanguageId) ?? AppConstants.defaultLanguage;

  const [getLocaleResources] = useLazyGetLocaleResourcesQuery();
  const [createOrEditUserConfig] = useCreateOrEditUserConfigMutation();

  useEffect(() => {
    getLocaleResources();
    changeLanguage(language);
    date.locale(locales[language]);
    document.documentElement.lang = language;
  }, [changeLanguage, date, getLocaleResources, language]);

  const items = useMemo(
    () => Object.keys(languages).map((key) => ({
      key,
      label: (
        <StyledFlagWrapper>
          <StyledFlag className="f24">
            <div className={`${key} lang flag`} />
          </StyledFlag>
          <StyledLanguageName>{languages[key as LanguageKey]}</StyledLanguageName>
        </StyledFlagWrapper>
      ),
    })),
    [],
  );

  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      createOrEditUserConfig({ key: 'languageId', configValue: key as LanguageKey });
      setOpen(false);
    },
    [createOrEditUserConfig],
  );

  const renderDropdown = useCallback(
    (): ReactNode => (
      <StyleFlagsWrapper>
        <Menu onClick={handleMenuClick} items={items} />
      </StyleFlagsWrapper>
    ),
    [handleMenuClick, items],
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      popupRender={renderDropdown}
      placement="bottomRight"
      trigger={['click']}
    >
      <StyledListItemHeader>
        <Tooltip placement="bottom" title={languages[language as LanguageKey]}>
          <div className="f24">
            <div className={`${language} lang flag`} />
          </div>
        </Tooltip>
      </StyledListItemHeader>
    </Dropdown>
  );
};

export default Languages;
